import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
  ChangeDetectionStrategy
} from '@angular/core';
import { Router } from '@angular/router';
import {
  faArrowLeft,
  faArrowRight,
  faBox,
  faComment,
  faPlane,
  faPlaneArrival,
  faPlaneDeparture,
  faTicket,
  faUsers,
  faUserTie,
  IconDefinition
} from '@fortawesome/free-solid-svg-icons';
import { IEnquiryFlight } from '@simplyfly/shared';
import { IEnquiry } from '@simplyfly/shared';
import { getDisplayedEnquiryRefTitle, getEnquiryRefTitleWithoutPrefix } from 'src/app/interfaces/enquiry.helpers';
import { EnquiryFlightService } from 'src/app/services/enquiry-flights/enquiry-flights.service';
import { LoaderService } from 'src/app/services/loader/loader.service';
import { EnumEnquiryType } from '@simplyfly/shared';
import { EnumLanguage } from '@simplyfly/shared';
import { PipedriveService } from 'src/app/services/pipedrive.service';
import { IPipedriveOrganization } from '@simplyfly/shared';
import { Subscription } from 'rxjs';
import { isBefore } from 'date-fns';
import { IUser } from '@simplyfly/shared';
import { getUserFullname } from 'src/app/interfaces/user.helpers';
import { UserService } from 'src/app/services/user/user.service';
import { AlertService } from 'src/app/services/alert/alert.service';
import { FlightBriefService } from 'src/app/services/flight-briefs/flight-briefs.service';
import { AppDataService } from 'src/app/services/data/app-data.service';
import { IFlightBrief } from '@simplyfly/shared';
import { IFbo } from '@simplyfly/shared';
import { IAirport } from '@simplyfly/shared';
import { AirportService } from 'src/app/services/airports/airports.service';
import { FboService } from 'src/app/services/fbos/fbos.service';
import { capitalize } from 'src/app/misc.utils';
import { AuthService } from 'src/app/services/auth/auth.service';
import { FaIconComponent } from '@fortawesome/angular-fontawesome';
import { EnquiryFlightsCardMiniComponent } from '../enquiry-flights-card-mini/enquiry-flights-card-mini.component';
import { EnquiryFlightEditAirportComponent } from './enquiry-flight-edit-airport/enquiry-flight-edit-airport.component';
import { EnquiryFlightEditDetailsComponent } from './enquiry-flight-edit-details/enquiry-flight-edit-details.component';
import { EnquiryFlightEditAircraftComponent } from './enquiry-flight-edit-aircraft/enquiry-flight-edit-aircraft.component';
import { EnquiryFlightEditPassengersComponent } from './enquiry-flight-edit-passengers/enquiry-flight-edit-passengers.component';
import { EnquiryFlightEditCargoComponent } from './enquiry-flight-edit-cargo/enquiry-flight-edit-cargo.component';
import { EnquiryFlightEditCrewComponent } from './enquiry-flight-edit-crew/enquiry-flight-edit-crew.component';
import { EnquiryFlightEditCommentComponent } from './enquiry-flight-edit-comment/enquiry-flight-edit-comment.component';
import { TranslateModule } from '@ngx-translate/core';

export enum EnumEnquiryFlightWizardStep {
  aircraft = 'aircraft',
  departure = 'departure',
  arrival = 'arrival',
  flightDetail = 'flightDetail',
  passengers = 'passengers',
  cargo = 'cargo',
  crew = 'crew',
  comment = 'comment'
}

export interface IEnquiryFlightCopyFieldGroup {
  id: string;
  labelKey: string;
  fields: Array<keyof IEnquiryFlight>;
}

const copyField = (
  id: string,
  fields: Array<keyof IEnquiryFlight>,
  labelKey: string = `ENQUIRY_FLIGHT_EDIT.COPY_FIELDS.FIELDS.${id.toUpperCase()}`
): IEnquiryFlightCopyFieldGroup => ({
  id,
  labelKey,
  fields
});

export const ENQUIRY_FLIGHT_COPY_FIELDS_BY_STEP: Partial<
  Record<EnumEnquiryFlightWizardStep, IEnquiryFlightCopyFieldGroup[]>
> = {
  [EnumEnquiryFlightWizardStep.aircraft]: [
    copyField('airline', ['countryCode', 'airlineId', 'airlineTitle'], 'FLIGHT_BRIEF.AIRLINE'),
    copyField('aircraft', [
      'aircraftModelId',
      'aircraftModelTitle',
      'aircraftId',
      'aircraftRegistration'
    ]),
    copyField('capacityMax', ['capacityMax'], 'ENQUIRY_FLIGHT_EDIT.AIRCRAFT.CAPACITY_MAX'),
    copyField(
      'displayAirline',
      ['displayAirlineOnFlightBrief'],
      'ENQUIRY_FLIGHT_EDIT.AIRCRAFT.DISPLAY_AIRLINE_ON_FLIGHT_BRIEF'
    ),
    copyField('toilets', ['aircraftHasToilet'], 'FLIGHT_BRIEF.WC'),
    copyField('smoking', ['smokingIsAllowed'], 'FLIGHT_BRIEF.SMOKING'),
    copyField(
      'aircraftPhotos',
      [
        'imageOutsideUrl',
        'imageOutsideZoomLevel',
        'imageInsideUrl',
        'imageInsideZoomLevel'
      ],
      'ENQUIRY_FLIGHT_EDIT.COPY_FIELDS.FIELDS.AIRCRAFTPHOTOS'
    )
  ],
  [EnumEnquiryFlightWizardStep.flightDetail]: [
    copyField('ferryFlight', ['isFerryFlight'], 'ENQUIRY_FLIGHTS.FERRY_FLIGHT'),
    copyField('flightNumber', ['flightNumber']),
    copyField('callsign', ['callsign'], 'ENQUIRY_FLIGHTS.CALLSIGN'),
    copyField('otherComment', ['otherComment'], 'FLIGHT_BRIEF.AIRPORT_INFORMATION.OTHER'),
    copyField(
      'checkInMessage',
      ['checkInScreenMessage'],
      'FLIGHT_BRIEF.AIRPORT_INFORMATION.CHECK_IN_SCREEN_MESSAGE'
    ),
    copyField('seatingPlan', ['seatingPlanType', 'seatingPlan'], 'FLIGHT_BRIEF.AIRPORT_INFORMATION.SEATING_PLAN'),
    copyField('boardingPass', ['boardingPassType', 'boardingPass'], 'FLIGHT_BRIEF.AIRPORT_INFORMATION.BOARDING_PASS'),
    copyField('passengerNationality', ['passengersNationality'], 'FLIGHT_BRIEF.AIRPORT_INFORMATION.PASSENGERS_NATIONALITY'),
    copyField('passengerType', ['kindOfPassengers'], 'FLIGHT_BRIEF.AIRPORT_INFORMATION.KIND_OF_PASSENGERS'),
    copyField('tripPurpose', ['targetOfTheTrip'], 'FLIGHT_BRIEF.AIRPORT_INFORMATION.TARGET_OF_THE_TRIP'),
    copyField('onboardOther', ['onboardOther'], 'FLIGHT_BRIEF.ONBOARD_INFORMATION.OTHER'),
    copyField('mandatoryIdCard', ['mandatoryCNI'], 'FLIGHT_BRIEF.AIRPORT_INFORMATION.IS_CNI_MANDATORY'),
    copyField('mandatoryPassport', ['mandatoryPassport'], 'FLIGHT_BRIEF.AIRPORT_INFORMATION.IS_PASSPORT_MANDATORY'),
    copyField('counters', ['counterCount', 'countersPerHour']),
    copyField('headrestCovers', ['headrestCovers'], 'FLIGHT_BRIEF.ONBOARD_INFORMATION.HEADREST_COVERS'),
    copyField('departureTransfer', ['departTransferGATBoardingPass'], 'FLIGHT_BRIEF.ONBOARD_INFORMATION.TRANSFER_GAT_BOARDING_PASS'),
    copyField('arrivalTransfer', ['arrivalTransferGATBoardingPass'], 'FLIGHT_BRIEF.ONBOARD_INFORMATION.TRANSFER_GAT_BOARDING_PASS'),
    copyField('departureTourLeader', ['departTourLeader', 'departTourLeaderPhone'], 'FLIGHT_BRIEF.ONBOARD_INFORMATION.TOUR_LEADER'),
    copyField('arrivalTourLeader', ['arrivalTourLeader', 'arrivalTourLeaderPhone'], 'FLIGHT_BRIEF.ONBOARD_INFORMATION.TOUR_LEADER'),
    copyField('vipSeating', ['vipSeatingFront'], 'FLIGHT_BRIEF.ONBOARD_INFORMATION.VIP_SEATING_FRONT'),
    copyField('departureReducedMobility', ['departReducedMobilityPassengers'], 'FLIGHT_BRIEF.ONBOARD_INFORMATION.REDUCED_MOBILITY_PASSENGERS'),
    copyField(
      'arrivalReducedMobility', ['arrivalReducedMobilityPassengers'],
      'FLIGHT_BRIEF.ONBOARD_INFORMATION.REDUCED_MOBILITY_PASSENGERS'
    ),
    copyField('oversizeBaggage', ['oversizeBaggage'], 'FLIGHT_BRIEF.ONBOARD_INFORMATION.OVERSIZE_BAGGAGE'),
    copyField('specialRemarks', ['specialRemarks'], 'FLIGHT_BRIEF.ONBOARD_INFORMATION.SPECIAL_REMARK'),
    copyField('onboardAnnouncement', ['onBoardFlightAnnouncement'], 'FLIGHT_BRIEF.ONBOARD_INFORMATION.ON_BOARD_FLIGHT_ANNOUNCEMENT')
  ],
  [EnumEnquiryFlightWizardStep.passengers]: [
    copyField('boardingTime', ['boardingMinutesBeforeDeparture'], 'ENQUIRY_FLIGHT_EDIT.PASSENGERS.BOARDING_MINUTES_BEFORE_LABEL'),
    copyField('pax', ['pax'], 'FLIGHT_BRIEF.AIRPORT_INFORMATION.NUMBER_OF_PASSENGERS'),
    copyField('passengerList', ['passengersId', 'passengers', 'disablePassengerSorting'], 'FLIGHTS.PASSENGERS'),
    copyField('pets', ['pets'], 'ENQUIRY_FLIGHT_EDIT.PASSENGERS.PETS_PLURAL'),
    copyField('catering', ['flightBriefCateringId', 'catering'], 'FLIGHT_BRIEF.ONBOARD_INFORMATION.CATERING_ON_BOARD'),
    copyField('beverages', ['beverages'], 'FLIGHT_BRIEF.ONBOARD_INFORMATION.BEVERAGES_ON_BOARD'),
    copyField('luggage', [
      'luggageQty',
      'luggagesWeight',
      'luggageIsCustom',
      'luggagesLimitInHoldInKg',
      'luggagesLimitInCabinInKg',
      'luggages'
    ], 'FLIGHT_BRIEF.AIRPORT_INFORMATION.LUGGAGES')
  ],
  [EnumEnquiryFlightWizardStep.cargo]: [
    copyField('weight', ['weight', 'weightInKg'], 'FLIGHT_BRIEF.WEIGHT'),
    copyField('volume', ['volume', 'volumeInM3'], 'FLIGHT_BRIEF.VOLUME'),
    copyField('cargoDelivery', ['deliveryCargoHoursBeforeDeparture'], 'ENQUIRY_FLIGHT_EDIT.CARGO.DELIVERY_LIMIT_HOURS_LABEL'),
    copyField('cargoType', ['typeOfCargo'], 'FLIGHT_BRIEF.CARGO_TYPE'),
    copyField('packageCount', ['nbPackages'], 'FLIGHT_BRIEF.NB_PACKAGES'),
    copyField('awb', ['awbNumber'], 'FLIGHT_BRIEF.AWB_NUMBERS'),
    copyField('customsDeclaration', ['customDeclaration'], 'FLIGHT_BRIEF.CUSTOMS_DECLARATION'),
    copyField('dgd', ['dgd'], 'FLIGHT_BRIEF.DGD')
  ],
  [EnumEnquiryFlightWizardStep.crew]: [
    copyField('salesPhone', ['salesH24Phone'], 'GENERAL.SALES_H24'),
    copyField('operationsPhone', ['opsH24Phone'], 'GENERAL.OPS_H24'),
    copyField('captain', ['captainId'], 'FLIGHT_BRIEF.CAPTAIN'),
    copyField('copilot', ['coPilotId'], 'FLIGHT_BRIEF.FIRST_OFFICER'),
    copyField('flightAttendants', ['stewardessEnabled', 'stewardessIds'], 'FLIGHT_BRIEF.FLIGHT_ATTENDANT'),
    copyField('departureSupervisors', ['departSupervisorEnabled', 'departSupervisorIds'], 'AIRLINE_SUPERVISORS.DEPARTURE'),
    copyField('arrivalSupervisors', ['arrivalSupervisorEnabled', 'arrivalSupervisorIds'], 'AIRLINE_SUPERVISORS.ARRIVAL')
  ],
  [EnumEnquiryFlightWizardStep.comment]: [copyField('comment', ['comment'], 'CALENDAR.REMARK')]
};

const cloneCopyValue = (value: any): any => {
  if (value instanceof Date) {
    return new Date(value.getTime());
  }

  if (Array.isArray(value)) {
    return value.map(cloneCopyValue);
  }

  if (value && typeof value === 'object') {
    const clone: Record<string, any> = {};
    for (const key of Object.keys(value)) {
      clone[key] = cloneCopyValue(value[key]);
    }
    return clone;
  }

  return value;
};

export const buildEnquiryFlightCopyPatch = (
  data: IEnquiryFlight,
  groups: IEnquiryFlightCopyFieldGroup[],
  selectedGroupIds: string[]
): Partial<IEnquiryFlight> => {
  const selectedGroupIdsSet = new Set(selectedGroupIds);
  const patch: Partial<IEnquiryFlight> = {};

  for (const group of groups) {
    if (!selectedGroupIdsSet.has(group.id)) {
      continue;
    }

    for (const field of group.fields) {
      if (Object.prototype.hasOwnProperty.call(data, field)) {
        patch[field] = cloneCopyValue(data[field]) as never;
      }
    }
  }

  return patch;
};

export const normalizeAircraftRegistration = (registration: string | null | undefined): string =>
  (registration || '').toUpperCase().replace(/[^A-Z0-9]/g, '');

export const allTargetFlightsUseSameAircraft = (
  sourceFlight: IEnquiryFlight,
  targetFlights: IEnquiryFlight[]
): boolean => {
  if (!targetFlights.length) {
    return false;
  }

  if (sourceFlight.aircraftId) {
    return targetFlights.every(targetFlight => targetFlight.aircraftId === sourceFlight.aircraftId);
  }

  const sourceRegistration = normalizeAircraftRegistration(sourceFlight.aircraftRegistration);

  return (
    !!sourceRegistration &&
    targetFlights.every(
      targetFlight =>
        normalizeAircraftRegistration(targetFlight.aircraftRegistration) === sourceRegistration
    )
  );
};

const areCopyValuesEqual = (valueA: any, valueB: any): boolean => {
  if (valueA === valueB) {
    return true;
  }

  if (valueA instanceof Date && valueB instanceof Date) {
    return valueA.getTime() === valueB.getTime();
  }

  if (Array.isArray(valueA) && Array.isArray(valueB)) {
    return (
      valueA.length === valueB.length &&
      valueA.every((item: any, index: number) => areCopyValuesEqual(item, valueB[index]))
    );
  }

  if (valueA && valueB && typeof valueA === 'object' && typeof valueB === 'object') {
    const keysA = Object.keys(valueA);
    const keysB = Object.keys(valueB);

    return (
      keysA.length === keysB.length &&
      keysA.every(
        (key: string) =>
          Object.prototype.hasOwnProperty.call(valueB, key) &&
          areCopyValuesEqual(valueA[key], valueB[key])
      )
    );
  }

  return false;
};

export const getChangedEnquiryFlightCopyGroups = (
  data: IEnquiryFlight,
  previousData: IEnquiryFlight | null,
  groups: IEnquiryFlightCopyFieldGroup[]
): IEnquiryFlightCopyFieldGroup[] => {
  if (!previousData) {
    return [];
  }

  return groups.filter((group: IEnquiryFlightCopyFieldGroup) =>
    group.fields.some(
      (field: keyof IEnquiryFlight) => !areCopyValuesEqual(data[field], previousData[field])
    )
  );
};

const getWizardStepLabelKey = (
  step: EnumEnquiryFlightWizardStep,
  enquiryType: EnumEnquiryType
): string => {
  switch (step) {
    default:
      return step;
    case EnumEnquiryFlightWizardStep.aircraft:
      return 'FLIGHT_BRIEF.AIRCRAFT';
    case EnumEnquiryFlightWizardStep.departure:
      return 'FLIGHT_BRIEF.DEPARTURE';
    case EnumEnquiryFlightWizardStep.arrival:
      return 'FLIGHT_BRIEF.ARRIVAL';
    case EnumEnquiryFlightWizardStep.flightDetail:
      return 'CALENDAR.OFFCANVAS_TITLE';
    case EnumEnquiryFlightWizardStep.passengers:
      return 'FLIGHTS.PASSENGERS';
    case EnumEnquiryFlightWizardStep.cargo:
      return 'QUOTATION.CARGO';
    case EnumEnquiryFlightWizardStep.crew:
      return [EnumEnquiryType.cargo, EnumEnquiryType.commercial].includes(enquiryType)
        ? 'GENERAL.OPERATOR'
        : 'CREWS.PLURAL';
    case EnumEnquiryFlightWizardStep.comment:
      return 'CALENDAR.REMARK';
  }
};

const getWizardStepIcon = (step: EnumEnquiryFlightWizardStep): IconDefinition => {
  switch (step) {
    default:
      return faPlaneDeparture;
    case EnumEnquiryFlightWizardStep.aircraft:
      return faPlane;
    case EnumEnquiryFlightWizardStep.departure:
      return faPlaneDeparture;
    case EnumEnquiryFlightWizardStep.arrival:
      return faPlaneArrival;
    case EnumEnquiryFlightWizardStep.flightDetail:
      return faTicket;
    case EnumEnquiryFlightWizardStep.passengers:
      return faUsers;
    case EnumEnquiryFlightWizardStep.cargo:
      return faBox;
    case EnumEnquiryFlightWizardStep.crew:
      return faUserTie;
    case EnumEnquiryFlightWizardStep.comment:
      return faComment;
  }
};

export const getEnquiryRefContractTitleWithoutPrefixForFlight = (enquiry: IEnquiry): string => {
  if (enquiry.refContractTitleWithoutPrefixDisplayed) {
    return enquiry.refContractTitleWithoutPrefixDisplayed;
  }

  if (enquiry.refContractTitleWithoutPrefix?.endsWith('-C')) {
    return enquiry.refContractTitleWithoutPrefix;
  }

  if (enquiry.refContractNumber) {
    return getEnquiryRefTitleWithoutPrefix(
      enquiry.refContractYear,
      enquiry.refContractMonth,
      enquiry.refContractNumber,
      2
    );
  }

  return enquiry.refContractTitleWithoutPrefix || '';
};

@Component({
    selector: 'app-enquiry-flight-edit',
    templateUrl: './enquiry-flight-edit.component.html',
    styleUrls: ['./enquiry-flight-edit.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [
      FaIconComponent,
      EnquiryFlightsCardMiniComponent,
      EnquiryFlightEditAirportComponent,
      EnquiryFlightEditDetailsComponent,
      EnquiryFlightEditAircraftComponent,
      EnquiryFlightEditPassengersComponent,
      EnquiryFlightEditCargoComponent,
      EnquiryFlightEditCrewComponent,
      EnquiryFlightEditCommentComponent,
      TranslateModule
    ]
})
export class EnquiryFlightEditComponent implements OnInit, OnChanges, OnDestroy {
  @Input() enquiry: IEnquiry | null = null;
  @Input() enquiryFlightId: string | null = null;

  get enquiryReference(): string | null {
    if (
      !this.enquiry ||
      (!this.enquiry.refEnquiryTitleDisplayed &&
        !this.enquiry.refEnquiryTitle &&
        !this.enquiry.refEnquiryNumber)
    ) {
      return null;
    }

    return getDisplayedEnquiryRefTitle(this.enquiry, 'refEnquiry');
  }

  get contractReference(): string | null {
    if (
      this.enquiry &&
      (this.enquiry.refContractTitleDisplayed ||
        this.enquiry.refContractTitle ||
        this.enquiry.refContractNumber)
    ) {
      return getDisplayedEnquiryRefTitle(this.enquiry, 'refContract');
    }

    return this.enquiryFlight?.enquiryRefContractTitle || null;
  }

  get clientName(): string | null {
    return this.enquiry?.clientTitle || this.enquiryFlight?.clientName || null;
  }
  @Input() inModal = false;
  @Input() currentStep: EnumEnquiryFlightWizardStep = EnumEnquiryFlightWizardStep.aircraft;

  @Output() updatedSelectedEnquiryFlight: EventEmitter<string> = new EventEmitter();

  @ViewChild('btnCloseModal') btnCloseModalEl: ElementRef;

  EnumEnquiryFlightWizardStep = EnumEnquiryFlightWizardStep;

  flightsForEnquiry: IEnquiryFlight[] = [];
  loadingFlightsForEnquiry = false;
  currentEnquiryFlightIndex = 0;

  enquiryFlight: IEnquiryFlight | null = null;

  faArrowLeft = faArrowLeft;
  faArrowRight = faArrowRight;

  steps: EnumEnquiryFlightWizardStep[] = [];

  clientsObj: { [id: string]: IPipedriveOrganization | null } = {};
  usersObj: { [id: string]: IUser | null } = {};
  currentUser: IUser | null = null;

  flightBriefsForEnquiry: { [enquiryId: string]: IFlightBrief[] } = {};
  loadingFlightBriefsForEnquiry: { [enquiryId: string]: boolean } = {};

  getWizardStepLabelKey = getWizardStepLabelKey;
  getWizardStepIcon = getWizardStepIcon;

  fbosObj: { [id: string]: IFbo | null } = {};
  airportsObj: { [id: string]: IAirport | null } = {};

  private subscriptions = new Subscription();
  private subscriptionsEnquiryFlight = new Subscription();

  constructor(
    private authService: AuthService,
    private router: Router,
    private loaderService: LoaderService,
    private enquiryFlightService: EnquiryFlightService,
    private pipedriveService: PipedriveService,
    private userService: UserService,
    private alertService: AlertService,
    private flightBriefService: FlightBriefService,
    private appDataService: AppDataService,
    private airportService: AirportService,
    private fboService: FboService
  ) {
    this.authService.userObservable.subscribe((user: IUser) => {
      this.currentUser = user;
    });
  }

  ngOnInit(): void {
    this.updatedEnquiry();
    this.loadEnquiryFlight();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (
      changes.enquiry &&
      (!changes.enquiry.previousValue ||
        (changes.enquiry.currentValue &&
          changes.enquiry.previousValue.id !== changes.enquiry.currentValue.id))
    ) {
      this.updatedEnquiry();
    }

    if (
      changes.enquiryFlightId &&
      changes.enquiryFlightId.previousValue !== changes.enquiryFlightId.currentValue
    ) {
      this.loadEnquiryFlight();

      if (!this.enquiryFlightId) {
        this.currentStep = EnumEnquiryFlightWizardStep.aircraft;
      }
    }
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
    this.subscriptionsEnquiryFlight.unsubscribe();
  }

  releaseModalFocus(): void {
    const activeElement: Element | null = document.activeElement;

    if (activeElement instanceof HTMLElement) {
      activeElement.blur();
    }
  }

  redirectAfterSaving(): void {
    this.router.navigate(['/admin/enquiries/' + this.enquiry.id]);
  }

  updateCurrentStep(step: EnumEnquiryFlightWizardStep): void {
    this.currentStep = step;
  }

  goPrev(): void {
    let newStep: EnumEnquiryFlightWizardStep | null = null;

    switch (this.currentStep) {
      case EnumEnquiryFlightWizardStep.departure:
        newStep = EnumEnquiryFlightWizardStep.aircraft;
        break;
      case EnumEnquiryFlightWizardStep.arrival:
        newStep = EnumEnquiryFlightWizardStep.departure;
        break;
      case EnumEnquiryFlightWizardStep.flightDetail:
        newStep = EnumEnquiryFlightWizardStep.arrival;
        break;
      case EnumEnquiryFlightWizardStep.passengers:
        newStep = EnumEnquiryFlightWizardStep.flightDetail;
        break;
      case EnumEnquiryFlightWizardStep.cargo:
        newStep = EnumEnquiryFlightWizardStep.flightDetail;
        break;
      case EnumEnquiryFlightWizardStep.crew:
        if (this.enquiryFlight.enquiryType === EnumEnquiryType.cargo) {
          newStep = EnumEnquiryFlightWizardStep.cargo;
        } else {
          newStep = EnumEnquiryFlightWizardStep.passengers;
        }
        break;
      case EnumEnquiryFlightWizardStep.comment:
        newStep = EnumEnquiryFlightWizardStep.crew;
        break;
    }

    if (newStep) {
      this.updateCurrentStep(newStep);
    }
  }

  async saveAndGoNext(data: IEnquiryFlight): Promise<void> {
    this.loaderService.presentLoader();
    const previousEnquiryFlight: IEnquiryFlight | null = this.enquiryFlight
      ? (cloneCopyValue(this.enquiryFlight) as IEnquiryFlight)
      : null;

    try {
      await this.save(data);

      await this.loaderService.hideLoaderOnSuccess();

      await this.updateOtherLegsIfConfirmed(data, previousEnquiryFlight);
      await this.updateFlightBriefAutomatically(data);

      let newStep: EnumEnquiryFlightWizardStep | null = null;

      switch (this.currentStep) {
        case EnumEnquiryFlightWizardStep.aircraft:
          newStep = EnumEnquiryFlightWizardStep.departure;
          break;
        case EnumEnquiryFlightWizardStep.departure:
          newStep = EnumEnquiryFlightWizardStep.arrival;
          break;
        case EnumEnquiryFlightWizardStep.arrival:
          newStep = EnumEnquiryFlightWizardStep.flightDetail;
          break;
        case EnumEnquiryFlightWizardStep.flightDetail:
          if (this.enquiryFlight.enquiryType === EnumEnquiryType.cargo) {
            newStep = EnumEnquiryFlightWizardStep.cargo;
          } else {
            newStep = EnumEnquiryFlightWizardStep.passengers;
          }
          break;
        case EnumEnquiryFlightWizardStep.passengers:
          newStep = EnumEnquiryFlightWizardStep.crew;
          break;
        case EnumEnquiryFlightWizardStep.cargo:
          newStep = EnumEnquiryFlightWizardStep.crew;
          break;
        case EnumEnquiryFlightWizardStep.crew:
          newStep = EnumEnquiryFlightWizardStep.comment;
          break;
      }

      if (newStep) {
        this.updateCurrentStep(newStep);
      } else if (this.currentStep === EnumEnquiryFlightWizardStep.comment) {
        if (this.inModal) {
          if (this.btnCloseModalEl) {
            this.btnCloseModalEl.nativeElement.click();
          }
        } else {
          this.redirectAfterSaving();
        }
      }
    } catch (err) {
      await this.loaderService.hideLoaderOnFailure(err.message);
    }
  }

  async updateFlightBriefAutomatically(data: IEnquiryFlight): Promise<void> {
    this.loaderService.presentLoader();

    try {
      const refreshedEnquiryFlights = this.getRefreshedFlightsForEnquiry(data);

      if (this.flightBriefsForEnquiry[data.enquiryId]?.length) {
        // Flight brief exists - update the existing one
        const existingFlightBrief = this.flightBriefsForEnquiry[data.enquiryId][0];

        const updatedFlightBrief = this.flightBriefService.prepareFlightBriefData(
          existingFlightBrief,
          refreshedEnquiryFlights,
          this.enquiry,
          this.fbosObj,
          this.airportsObj,
          this.currentUser
        );

        await this.applyAircraftDataToFlightBrief(updatedFlightBrief, data, refreshedEnquiryFlights);

        await this.flightBriefService.update(updatedFlightBrief);
      } else {
        // No flight brief exists - create new one with version 1
        const newFlightBrief: IFlightBrief = {
          enquiryId: data.enquiryId,
          language: EnumLanguage.fr,
          versionNumber: 1,
          comments: null,
          internalNote: null,
          googleMapStaticUrl: null,
          aircraftModelId: null,
          aircraftModelTitle: null,
          aircraftId: null,
          aircraftRegistration: null,
          imageOutsideUrl: null,
          imageInsideUrl: null,
          imageOutsideZoomLevel: 1,
          imageInsideZoomLevel: 1
        } as IFlightBrief;

        const preparedFlightBrief = this.flightBriefService.prepareFlightBriefData(
          newFlightBrief,
          refreshedEnquiryFlights,
          this.enquiry,
          this.fbosObj,
          this.airportsObj,
          this.currentUser
        );

        await this.applyAircraftDataToFlightBrief(preparedFlightBrief, data, refreshedEnquiryFlights);

        await this.flightBriefService.create(preparedFlightBrief);
      }

      await this.loaderService.hideLoaderOnSuccess();
    } catch (err) {
      await this.loaderService.hideLoaderOnFailure(err);
    }
  }

  private getRefreshedFlightsForEnquiry(savedData: IEnquiryFlight): IEnquiryFlight[] {
    const flights = this.flightsForEnquiry.map((enquiryFlight: IEnquiryFlight) => {
      if (savedData.id && enquiryFlight.id === savedData.id) {
        return {
          ...enquiryFlight,
          ...savedData
        };
      }

      return enquiryFlight;
    });

    if (
      savedData.id &&
      !flights.some((enquiryFlight: IEnquiryFlight) => enquiryFlight.id === savedData.id)
    ) {
      flights.push(savedData);
    }

    return flights;
  }

  private async applyAircraftDataToFlightBrief(
    flightBrief: IFlightBrief,
    savedData: IEnquiryFlight,
    enquiryFlights: IEnquiryFlight[]
  ): Promise<void> {
    const shouldRefreshAircraftData =
      !flightBrief.id ||
      typeof savedData.aircraftId !== 'undefined' ||
      typeof savedData.aircraftModelId !== 'undefined';

    if (!shouldRefreshAircraftData) {
      return;
    }

    const sourceFlight: IEnquiryFlight | null =
      savedData.aircraftId || savedData.aircraftModelId
        ? savedData
        : enquiryFlights.find((enquiryFlight: IEnquiryFlight) => !!enquiryFlight.aircraftId) ||
          enquiryFlights.find(
            (enquiryFlight: IEnquiryFlight) => !!enquiryFlight.aircraftModelId
          ) ||
          null;

    if (!sourceFlight) {
      return;
    }

    flightBrief.airlineId = sourceFlight.airlineId;
    flightBrief.airlineTitle = sourceFlight.airlineTitle;
    flightBrief.aircraftModelId = sourceFlight.aircraftModelId;
    flightBrief.aircraftModelTitle = sourceFlight.aircraftModelTitle;
    flightBrief.aircraftId = sourceFlight.aircraftId;
    flightBrief.aircraftRegistration = sourceFlight.aircraftRegistration;
    flightBrief.aircraftHasToilet = sourceFlight.aircraftHasToilet;
    flightBrief.smokingIsAllowed = sourceFlight.smokingIsAllowed;
    flightBrief.displayAirlineOnFlightBrief = sourceFlight.displayAirlineOnFlightBrief;
  }

  async updateOtherLegsIfConfirmed(
    data: IEnquiryFlight,
    previousData: IEnquiryFlight | null = this.enquiryFlight
  ): Promise<void> {
    const stepsToIgnore: EnumEnquiryFlightWizardStep[] = [
      EnumEnquiryFlightWizardStep.departure,
      EnumEnquiryFlightWizardStep.arrival
    ];

    const groups = ENQUIRY_FLIGHT_COPY_FIELDS_BY_STEP[this.currentStep] || [];
    const changedGroups = getChangedEnquiryFlightCopyGroups(data, previousData, groups);
    const targetFlights = this.flightsForEnquiry.filter(
      (enquiryFlight: IEnquiryFlight) => enquiryFlight.id !== data.id
    );

    if (
      stepsToIgnore.includes(this.currentStep) ||
      !targetFlights.length ||
      !changedGroups.length
    ) {
      return;
    }

    const selectedGroupIds = await this.alertService.presentCheckboxSelection(
      'ENQUIRY_FLIGHT_EDIT.COPY_FIELDS.TITLE',
      'ENQUIRY_FLIGHT_EDIT.COPY_FIELDS.MESSAGE',
      changedGroups.map(group => ({
        title: group.labelKey,
        value: group.id,
        checked:
          group.id === 'aircraftPhotos'
            ? allTargetFlightsUseSameAircraft(data, targetFlights)
            : true
      })),
      'ENQUIRY_FLIGHT_EDIT.COPY_FIELDS.DO_NOT_APPLY'
    );

    if (!selectedGroupIds?.length) {
      return;
    }

    const patch = buildEnquiryFlightCopyPatch(data, changedGroups, selectedGroupIds);

    this.loaderService.presentLoader();

    await Promise.all(
      targetFlights.map((enquiryFlight: IEnquiryFlight) =>
        this.enquiryFlightService.update({
          ...cloneCopyValue(patch),
          id: enquiryFlight.id
        } as IEnquiryFlight)
      )
    );

    this.flightsForEnquiry = this.flightsForEnquiry.map((enquiryFlight: IEnquiryFlight) =>
      enquiryFlight.id === data.id
        ? enquiryFlight
        : ({ ...enquiryFlight, ...cloneCopyValue(patch) } as IEnquiryFlight)
    );

    await this.loaderService.hideLoaderOnSuccess();
  }

  private async save(data: IEnquiryFlight): Promise<void> {
    try {
      for (const field in data) {
        if (typeof data[field] === 'undefined') {
          data[field] = null;
        }
      }

      if (this.enquiry) {
        data.enquiryId = this.enquiry.id;
        data.enquiryType = this.enquiry.type;
        data.enquiryRefContractTitle = getDisplayedEnquiryRefTitle(this.enquiry, 'refContract');
        data.enquiryRefContractTitleWithoutPrefix =
          getEnquiryRefContractTitleWithoutPrefixForFlight(this.enquiry);
        data.enquiryRefContractYear = this.enquiry.refContractYear;
        data.enquiryRefContractMonth = this.enquiry.refContractMonth;
        data.enquiryRefContractMonth = this.enquiry.refContractMonth;
        data.clientId = this.enquiry.clientId.toString();
        data.clientName = this.clientsObj[data.clientId]?.name || null;
        data.clientContactId = this.enquiry.contactId.toString();
        data.clientContactTitle = this.enquiry.contactTitle;
        data.enquiryProcessedBy = this.enquiry.processedBy;
        data.userFullName = getUserFullname(this.usersObj[this.enquiry.processedBy]);
        data.userInitials = this.usersObj[this.enquiry.processedBy]?.initials || '';
      }

      let promise;
      if (this.enquiryFlightId) {
        data.id = this.enquiryFlightId;
        promise = () => this.enquiryFlightService.update(data);
      } else {
        promise = () => this.enquiryFlightService.create(data);
      }

      const id: string = await promise();

      if (!this.enquiryFlightId) {
        this.enquiryFlightId = id;
        data.id = id;

        this.updatedSelectedEnquiryFlight.emit(this.enquiryFlightId);
      }

      const savedFlight = {
        ...(this.enquiryFlight ?? {}),
        ...data,
        id: this.enquiryFlightId
      } as IEnquiryFlight;
      this.enquiryFlight = savedFlight;

      const savedFlightIndex = this.flightsForEnquiry.findIndex(
        enquiryFlight => enquiryFlight.id === savedFlight.id
      );

      if (savedFlightIndex >= 0) {
        this.flightsForEnquiry = this.flightsForEnquiry.map(enquiryFlight =>
          enquiryFlight.id === savedFlight.id ? savedFlight : enquiryFlight
        );
      } else {
        this.flightsForEnquiry = [...this.flightsForEnquiry, savedFlight];
      }
    } catch (err) {
      return Promise.reject(err);
    }
  }

  updatedEnquiry(): void {
    this.updateSteps();

    if (this.enquiry?.clientId) {
      this.loadClient(this.enquiry.clientId.toString());
    }

    this.loadUser(this.enquiry?.processedBy);

    this.loadFlightsForEnquiry();
  }

  async loadClient(clientId: string): Promise<void> {
    if (typeof this.clientsObj[clientId] === 'undefined') {
      this.clientsObj[clientId] = null;

      this.clientsObj[clientId] = await this.pipedriveService.getOrganization(clientId);
    }
  }

  loadUser(userId: string): void {
    if (typeof this.usersObj[userId] === 'undefined') {
      this.usersObj[userId] = null;

      this.subscriptions.add(
        this.userService.getFromId(userId).subscribe((user: IUser | null) => {
          this.usersObj[userId] = user;
        })
      );
    }
  }

  stepIsDisabled(step: EnumEnquiryFlightWizardStep): boolean {
    if (this.enquiryFlight) {
      const conditions: boolean[] = [];

      switch (step) {
        case EnumEnquiryFlightWizardStep.departure:
          conditions.push(!this.enquiryFlight.airlineId);
          conditions.push(!this.enquiryFlight.aircraftModelId);
          break;
        case EnumEnquiryFlightWizardStep.arrival:
          conditions.push(!this.enquiryFlight.airportDepartId);
          break;
        case EnumEnquiryFlightWizardStep.flightDetail:
          conditions.push(!this.enquiryFlight.airportDepartId);
          conditions.push(!this.enquiryFlight.airportArrivalId);
          break;
        case EnumEnquiryFlightWizardStep.passengers:
          conditions.push(!this.enquiryFlight.airportDepartId);
          conditions.push(!this.enquiryFlight.airportArrivalId);
          break;
        case EnumEnquiryFlightWizardStep.cargo:
          conditions.push(!this.enquiryFlight.airportDepartId);
          conditions.push(!this.enquiryFlight.airportArrivalId);
          break;
        case EnumEnquiryFlightWizardStep.crew:
          conditions.push(!this.enquiryFlight.airportDepartId);
          conditions.push(!this.enquiryFlight.airportArrivalId);
          break;
        case EnumEnquiryFlightWizardStep.comment:
          conditions.push(!this.enquiryFlight.airportDepartId);
          conditions.push(!this.enquiryFlight.airportArrivalId);
          break;
      }

      return conditions.includes(true);
    }

    return step !== EnumEnquiryFlightWizardStep.aircraft;
  }

  updateSteps(): void {
    this.steps = [];

    if (this.enquiry) {
      for (const step of Object.values(EnumEnquiryFlightWizardStep)) {
        switch (step) {
          default:
            this.steps.push(step);
            break;
          case EnumEnquiryFlightWizardStep.passengers:
            if (this.enquiry.type !== EnumEnquiryType.cargo) {
              this.steps.push(step);
            }
            break;
          case EnumEnquiryFlightWizardStep.cargo:
            if (this.enquiry.type === EnumEnquiryType.cargo) {
              this.steps.push(step);
            }
            break;
        }
      }
    }
  }

  updateCurrentEnquiryFlightIndex(): void {
    this.currentEnquiryFlightIndex = 0;

    if (this.enquiryFlightId && this.flightsForEnquiry.length) {
      for (let i = 0; i < this.flightsForEnquiry.length; i++) {
        if (this.enquiryFlightId === this.flightsForEnquiry[i].id) {
          this.currentEnquiryFlightIndex = i;
          break;
        }
      }
    }
  }

  loadFlightsForEnquiry(): void {
    if (this.enquiry) {
      this.flightsForEnquiry = [];
      this.loadingFlightsForEnquiry = true;

      this.subscriptions.add(
        this.enquiryFlightService
          .getAllForEnquiry(this.enquiry.id)
          .subscribe((enquiryFlights: IEnquiryFlight[]) => {
            this.flightsForEnquiry = enquiryFlights;

            this.flightsForEnquiry.sort((a: IEnquiryFlight, b: IEnquiryFlight) => {
              if (a.departDateLocal < b.departDateLocal) {
                return -1;
              } else if (a.departDateLocal > b.departDateLocal) {
                return 1;
              } else {
                if (a.departTimeLocal < b.departTimeLocal) {
                  return -1;
                } else if (a.departTimeLocal > b.departTimeLocal) {
                  return 1;
                }
              }

              return 0;
            });

            for (const enquiryFlight of this.flightsForEnquiry) {
              for (const airportField of ['depart', 'arrival']) {
                if (enquiryFlight['airport' + capitalize(airportField) + 'Id']) {
                  this.loadAirport(enquiryFlight['airport' + capitalize(airportField) + 'Id']);
                }

                if (
                  enquiryFlight[airportField + 'FboEnabled'] &&
                  enquiryFlight[airportField + 'FboId']
                ) {
                  this.loadFbo(enquiryFlight[airportField + 'FboId']);
                }
              }
            }

            this.updateCurrentEnquiryFlightIndex();

            this.loadingFlightsForEnquiry = false;
          })
      );
    }
  }

  prevEnquiryFlight(): void {
    if (this.enquiryFlightId && this.flightsForEnquiry && this.flightsForEnquiry?.length) {
      for (let i = 0; i < this.flightsForEnquiry.length; i++) {
        if (i > 0 && this.enquiryFlightId === this.flightsForEnquiry[i].id) {
          this.updatedSelectedEnquiryFlight.emit(this.flightsForEnquiry[i - 1].id);
          break;
        }
      }
    }
  }

  nextEnquiryFlight(): void {
    if (this.enquiryFlightId && this.flightsForEnquiry.length) {
      for (let i = 0; i < this.flightsForEnquiry.length; i++) {
        if (
          i < this.flightsForEnquiry.length - 1 &&
          this.enquiryFlightId === this.flightsForEnquiry[i].id
        ) {
          this.updatedSelectedEnquiryFlight.emit(this.flightsForEnquiry[i + 1].id);
          break;
        }
      }
    }
  }

  loadEnquiryFlight(): void {
    if (this.enquiryFlightId) {
      if (this.subscriptionsEnquiryFlight) {
        this.subscriptionsEnquiryFlight.unsubscribe();
      }

      this.subscriptionsEnquiryFlight = new Subscription();

      this.subscriptionsEnquiryFlight.add(
        this.enquiryFlightService
          .getFromId(this.enquiryFlightId)
          .subscribe((enquiryFlight: IEnquiryFlight | null) => {
            this.enquiryFlight = enquiryFlight;

            this.loadFlightBriefForEnquiry(this.enquiryFlight.enquiryId);

            this.updateCurrentEnquiryFlightIndex();
          })
      );
    } else {
      this.enquiryFlight = null;
    }
  }

  loadFlightBriefForEnquiry(enquiryId: string): void {
    if (typeof this.flightBriefsForEnquiry[enquiryId] === 'undefined') {
      this.flightBriefsForEnquiry[enquiryId] = [];
      this.loadingFlightBriefsForEnquiry[enquiryId] = true;

      this.subscriptions.add(
        this.flightBriefService
          .getAllForEnquiry(enquiryId)
          .subscribe((flightBriefs: IFlightBrief[]) => {
            this.flightBriefsForEnquiry[enquiryId] = flightBriefs;

            this.flightBriefsForEnquiry[enquiryId].sort((a, b) => (a.created > b.created ? -1 : 1));

            this.loadingFlightBriefsForEnquiry[enquiryId] = false;
          })
      );
    }
  }

  loadAirport(airportId: string): void {
    if (typeof this.airportsObj[airportId] === 'undefined') {
      this.airportsObj[airportId] = null;

      this.subscriptions.add(
        this.airportService.getFromId(airportId).subscribe((airport: IAirport) => {
          this.airportsObj[airportId] = airport;
        })
      );
    }
  }

  loadFbo(fboId: string): void {
    if (typeof this.fbosObj[fboId] === 'undefined') {
      this.fbosObj[fboId] = null;

      this.subscriptions.add(
        this.fboService.getFromId(fboId).subscribe((fbo: IFbo) => {
          this.fbosObj[fboId] = fbo;
        })
      );
    }
  }
}
